import { useEffect, useState } from "react";
import { validateEmail } from "@/lib/utils/input-validator"; // Import the validation method
import { useNavigate } from "react-router-dom";
import { useTranslations } from "next-intl";
import { translateBackendError } from "@/lib/i18n/translateBackendError";
import { requestPasswordReset } from "@/lib/api/handlers/AuthenticationHandling";
import { useRateLimitCountdown } from "@/hooks/useRateLimitCountdown";

/**
 * Initial state for the reset password form
 * Contains empty value for email field
 */
const initialState = {
  email: "",
};

/**
 * Custom hook for managing password reset form state and behavior
 * Handles form state, validation, error handling, and submission
 * @returns Form state and handler functions for the reset password form
 */
export function useResetPasswordForm() {
  const navigate = useNavigate();
  // State for form field values
  const [formData, setFormData] = useState(initialState);
  // State for validation errors by field
  const [errors, setErrors] = useState<{ [key: string]: string | undefined }>(
    {}
  );
  // Loading state during form submission
  const [isLoading, setIsLoading] = useState(false);
  // Rate-limit countdown state
  const rateLimit = useRateLimitCountdown();
  // Get translation function for error messages
  const t = useTranslations("errors");

  // Keep countdown banner in sync with clock tick
  useEffect(() => {
    if (!rateLimit.isLimited) return;
    setErrors((prev) => ({
      ...prev,
      email: t("rateLimitedCountdown", { seconds: rateLimit.secondsLeft }),
    }));
  }, [rateLimit.secondsLeft, rateLimit.isLimited, t]);

  /**
   * Updates form state when input values change
   * Also clears existing errors for the changed field if it passes validation
   * @param field - The form field being updated
   * @param value - The new value for the field
   */
  function handleInputChange(field: string, value: string) {
    // Update the form data with the new value
    setFormData((prevState) => ({
      ...prevState,
      [field]: value,
    }));

    // If the field previously had an error, validate it again
    if (errors[field]) {
      let errorMessage = "";
      if (field === "email") {
        errorMessage = validateEmail(value, t);
      }

      // Clear the error if the field is now valid
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: errorMessage || undefined,
      }));
    }
  }

  /**
   * Validates all form fields and returns any validation errors
   * @returns Object containing validation errors for each field
   */
  function validateForm() {
    const validationErrors: { email?: string } = {};

    // Validate email
    const emailError = validateEmail(formData.email, t);
    if (emailError) {
      validationErrors.email = emailError;
    }

    return validationErrors;
  }

  /**
   * Handles form submission, including validation and API calls
   * On successful password reset request, redirects to the verification page
   * On failure, displays error message
   * @param e - Form submission event
   */
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (rateLimit.isLimited) return;

    const validationErrors = validateForm();
    if (Object.keys(validationErrors)?.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setIsLoading(true);

    try {
      const response = await requestPasswordReset(formData.email);

      // Known + unknown emails both resolve to `void` from the server action —
      // the backend silently succeeds on a non-existent user to prevent
      // account enumeration. Only true 5xx/network faults or rate-limit 429s
      // reach this branch.
      if (response && response.isServerError) {
        if (response.message === "rateLimited" && response.retryAfter) {
          rateLimit.start(response.retryAfter);
          setErrors({
            email: t("rateLimitedCountdown", { seconds: response.retryAfter }),
          });
        } else {
          setErrors({ email: translateBackendError(t, response) });
        }
        return;
      }

      window.dispatchEvent(new Event("navigationStart"));
      navigate("/auth/check-email");
    } catch (error) {
      setErrors({ email: translateBackendError(t, error instanceof Error ? error : "unknown") });
    } finally {
      setIsLoading(false);
    }
  }

  /**
   * Prefetches the check-email page for faster navigation
   * Called when hovering over the submit button
   */
  function prefetch() {
  }

  // Return the form state and handlers
  return {
    formData,
    errors,
    isLoading,
    handleInputChange,
    handleSubmit,
    prefetch,
    rateLimited: rateLimit.isLimited,
  };
}
